import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';
import { useSupabaseStorage } from '../../../hooks/useSupabaseStorage';
import { supabase } from '../../../lib/supabase';

interface UploadedFile {
  url: string;
  path: string;
  fileName: string;
  type: 'image' | 'video';
  thumbnailUrl?: string;
  thumbnailPath?: string;
}

const LeVeauDorPage = () => {
  const { t, i18n } = useTranslation();
  const { uploadFile, deleteFile } = useSupabaseStorage();
  const [uploadedFiles, setUploadedFiles] = useState<{ [key: number]: UploadedFile }>({});
  const [captions, setCaptions] = useState<{ [key: number]: string }>({});
  const [expandedBox, setExpandedBox] = useState<number | null>(null);
  const [uploadingBoxes, setUploadingBoxes] = useState<{ [key: number]: boolean }>({});
  const [uploadingThumbnails, setUploadingThumbnails] = useState<{ [key: number]: boolean }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [savingCaptions, setSavingCaptions] = useState<{ [key: number]: boolean }>({});
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const fileInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const thumbnailInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const captionTimeouts = useRef<{ [key: number]: NodeJS.Timeout }>({});

  const projectLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Wild Cherry', href: '/project/wildcherry' },
    { name: "Le Veau d'Or", href: '/project/leveaudor' },
    { name: 'Le Rock', href: '/project/lerock' },
    { name: 'Frenchette', href: '/project/frenchette' }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": "Le Veau d'Or - Creative Design Project",
    "description": "Le Veau d'Or project by Dava Nasr - Creative graphic design and brand development",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/project/leveaudor`,
    "creator": {
      "@type": "Person",
      "name": "Dava Nasr",
      "jobTitle": "Graphic Designer"
    },
    "image": "https://static.readdy.ai/image/b035185ce40665b0d42a9231037815e9/f1b7b09b3699e1a83975b65fbc7fad20.png"
  };

  // Get current language
  const currentLanguage = i18n.language;
  const isEnglish = currentLanguage === 'en';

  const handleMobileNavClick = (href: string) => {
    setIsMenuOpen(false);
    // Scroll to top on mobile when navigating
    if (window.innerWidth < 768) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const loadExistingFiles = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('web_design_uploads')
        .select('*')
        .order('box_index');

      if (error) {
        console.error('Database load error:', error);
        setError(`Failed to load files: ${error.message}`);
        return;
      }

      if (data && data.length > 0) {
        const filesMap: { [key: number]: UploadedFile } = {};
        const captionsMap: { [key: number]: string } = {};
        data.forEach((file) => {
          filesMap[file.box_index] = {
            url: file.file_url,
            path: file.file_path,
            fileName: file.file_name,
            type: file.file_type as 'image' | 'video',
            thumbnailUrl: file.thumbnail_url || undefined,
            thumbnailPath: file.thumbnail_path || undefined,
          };
          // Load language-specific caption
          const caption = isEnglish ? file.caption_en : file.caption_fr;
          if (caption) {
            captionsMap[file.box_index] = caption;
          }
        });
        setUploadedFiles(filesMap);
        setCaptions(captionsMap);
        console.log('Loaded captions for language:', currentLanguage, captionsMap);
      }
    } catch (error) {
      console.error('Error loading files:', error);
      setError(`Error loading files: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const saveFileToDatabase = async (boxIndex: number, fileData: UploadedFile, caption?: string) => {
    try {
      const updateData: any = {
        box_index: boxIndex,
        file_name: fileData.fileName,
        file_path: fileData.path,
        file_url: fileData.url,
        file_type: fileData.type,
        thumbnail_url: fileData.thumbnailUrl || null,
        thumbnail_path: fileData.thumbnailPath || null,
        uploaded_at: new Date().toISOString()
      };

      // Add language-specific caption
      if (caption !== undefined) {
        if (isEnglish) {
          updateData.caption_en = caption;
        } else {
          updateData.caption_fr = caption;
        }
      }

      const { data, error } = await supabase
        .from('web_design_uploads')
        .upsert(updateData, {
          onConflict: 'box_index'
        })
        .select();

      if (error) {
        console.error('Database save error:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Database save error:', error);
      throw error;
    }
  };

  const saveCaptionToDatabase = async (boxIndex: number, caption: string) => {
    try {
      setSavingCaptions(prev => ({ ...prev, [boxIndex]: true }));
      
      const { data: existingData, error: selectError } = await supabase
        .from('web_design_uploads')
        .select('*')
        .eq('box_index', boxIndex)
        .single();

      if (selectError && selectError.code !== 'PGRST116') {
        throw selectError;
      }

      const updateData: any = {};
      
      // Update language-specific caption
      if (isEnglish) {
        updateData.caption_en = caption;
      } else {
        updateData.caption_fr = caption;
      }

      if (existingData) {
        const { error } = await supabase
          .from('web_design_uploads')
          .update(updateData)
          .eq('box_index', boxIndex);

        if (error) {
          throw new Error(`Caption update error: ${error.message}`);
        }
      } else {
        const insertData = {
          box_index: boxIndex,
          file_name: '',
          file_path: '',
          file_url: '',
          file_type: 'image',
          uploaded_at: new Date().toISOString(),
          ...updateData
        };

        const { error } = await supabase
          .from('web_design_uploads')
          .insert(insertData);

        if (error) {
          throw new Error(`Caption insert error: ${error.message}`);
        }
      }

      console.log(`Caption saved for box ${boxIndex} in ${currentLanguage}:`, caption);
    } catch (error) {
      console.error('Caption save error:', error);
      throw error;
    } finally {
      setSavingCaptions(prev => ({ ...prev, [boxIndex]: false }));
    }
  };

  const removeFileFromDatabase = async (boxIndex: number) => {
    try {
      const { error } = await supabase
        .from('web_design_uploads')
        .delete()
        .eq('box_index', boxIndex);

      if (error) {
        console.error('Database delete error:', error);
        throw new Error(`Database error: ${error.message}`);
      }
    } catch (error) {
      console.error('Database delete error:', error);
      throw error;
    }
  };

  useEffect(() => {
    loadExistingFiles();
  }, []);

  const handleFileUpload = async (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      console.log('Starting file upload for box:', index, 'File:', file.name);
      setUploadingBoxes(prev => ({ ...prev, [index]: true }));
      setError(null);
      
      const result = await uploadFile(file);
      console.log('File uploaded to storage:', result);
      
      const fileType = index === 3 ? 'video' : 'image';
      
      const fileData: UploadedFile = {
        url: result.url,
        path: result.path,
        fileName: result.fileName,
        type: fileType
      };

      await saveFileToDatabase(index, fileData, captions[index] || '');
      
      setUploadedFiles(prev => ({ 
        ...prev, 
        [index]: fileData
      }));
      
      console.log('Upload completed successfully');
      
    } catch (error) {
      console.error('Upload error:', error);
      setError(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploadingBoxes(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleThumbnailUpload = async (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const existingFile = uploadedFiles[index];
    if (!existingFile || existingFile.type !== 'video') return;

    try {
      console.log('Starting thumbnail upload for box:', index, 'File:', file.name);
      setUploadingThumbnails(prev => ({ ...prev, [index]: true }));
      setError(null);

      const result = await uploadFile(file);
      console.log('Thumbnail uploaded to storage:', result);

      const updatedFileData: UploadedFile = {
        ...existingFile,
        thumbnailUrl: result.url,
        thumbnailPath: result.path,
      };

      await saveFileToDatabase(index, updatedFileData, captions[index] || '');

      setUploadedFiles(prev => ({
        ...prev,
        [index]: updatedFileData,
      }));

      console.log('Thumbnail upload completed successfully');
    } catch (error) {
      console.error('Thumbnail upload failed:', error);
      setError(`Thumbnail upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploadingThumbnails(prev => ({ ...prev, [index]: false }));
      if (thumbnailInputRefs.current[index]) {
        thumbnailInputRefs.current[index]!.value = '';
      }
    }
  };

  const handleFileDelete = async (index: number) => {
    try {
      setError(null);
      
      const file = uploadedFiles[index];
      if (!file) return;

      await deleteFile(file.path);
      
      if (file.thumbnailPath) {
        await deleteFile(file.thumbnailPath);
      }
      
      await removeFileFromDatabase(index);
      
      setUploadedFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[index];
        return newFiles;
      });
      
      setCaptions(prev => {
        const newCaptions = { ...prev };
        delete newCaptions[index];
        return newCaptions;
      });
      
      setExpandedBox(null);
      
    } catch (error) {
      console.error('Delete error:', error);
      setError(`Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleRemoveFile = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    handleFileDelete(index);
  };

  // Reload captions when language changes
  useEffect(() => {
    loadExistingFiles();
  }, [currentLanguage]);

  const handleCaptionChange = (index: number, value: string) => {
    setCaptions(prev => ({ ...prev, [index]: value }));
    
    if (captionTimeouts.current[index]) {
      clearTimeout(captionTimeouts.current[index]);
    }
    
    captionTimeouts.current[index] = setTimeout(async () => {
      try {
        await saveCaptionToDatabase(index, value);
        console.log(`Auto-saved caption for box ${index} in ${currentLanguage}`);
      } catch (error) {
        console.error('Failed to auto-save caption:', error);
        setError(`Failed to save caption: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }, 1000);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
  };

  const resetZoom = () => {
    setZoomLevel(1);
    setZoomPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - zoomPosition.x, y: e.clientY - zoomPosition.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      setZoomPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const renderUploadBox = (index: number) => {
    const file = uploadedFiles[index];
    const isUploading = uploadingBoxes[index];
    const isSavingCaption = savingCaptions[index];
    const isVideo = index === 3;

    return (
      <div key={index} className="space-y-4">
        <div
          className={`relative bg-gray-800 border-2 border-gray-600 transition-all duration-300 cursor-pointer hover:border-gray-500 ${
            isVideo ? 'aspect-video' : 'aspect-auto md:aspect-square'
          } h-auto md:h-60 lg:h-80`}
          onClick={() => {
            if (uploadedFiles[index]) {
              // Only allow expand on desktop (md and above)
              if (window.innerWidth >= 768) {
                setExpandedBox(index);
              }
            } else {
              fileInputRefs.current[index]?.click();
            }
          }}
        >
          {uploadedFiles[index] ? (
            <div className="relative w-full h-full">
              {uploadedFiles[index].type === 'video' ? (
                <>
                  {/* Mobile Video - Show thumbnail with play functionality */}
                  <div className="block md:hidden w-full h-full">
                    {uploadedFiles[index].thumbnailUrl ? (
                      <div 
                        className="relative w-full h-full cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Create and play video element
                          const video = document.createElement('video');
                          video.src = uploadedFiles[index].url;
                          video.controls = true;
                          video.autoplay = true;
                          video.playsInline = true;
                          video.className = "w-full h-full object-cover";
                          video.style.position = 'absolute';
                          video.style.top = '0';
                          video.style.left = '0';
                          video.style.zIndex = '10';
                          e.currentTarget.appendChild(video);
                          video.play();
                        }}
                      >
                        <img 
                          src={uploadedFiles[index].thumbnailUrl} 
                          alt="Video thumbnail"
                          className="w-full h-full object-contain"
                        />
                        {/* Play Button Overlay for Mobile */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-black/25 rounded-full p-4">
                            <i className="ri-play-fill text-white text-4xl w-8 h-8 flex items-center justify-center"></i>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <video 
                        src={uploadedFiles[index].url} 
                        className="w-full h-full object-cover" 
                        controls
                        playsInline
                        preload="metadata"
                        style={{ outline: 'none' }}
                      />
                    )}
                  </div>
                  
                  {/* Desktop Video - Show thumbnail with overlay if available */}
                  <div className="hidden md:block w-full h-full">
                    {uploadedFiles[index].thumbnailUrl ? (
                      <>
                        <img 
                          src={uploadedFiles[index].thumbnailUrl} 
                          alt="Video thumbnail"
                          className="w-full h-full object-[center_20%]"
                        />
                        {/* Video Play Button Overlay - Only show on desktop */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="bg-black bg-opacity-60 rounded-full p-4">
                            <i className="ri-play-fill text-white text-4xl w-8 h-8 flex items-center justify-center"></i>
                          </div>
                        </div>
                      </>
                    ) : (
                      <video 
                        src={uploadedFiles[index].url} 
                        className="w-full h-full object-cover" 
                        controls
                        playsInline
                        preload="metadata"
                        style={{ outline: 'none' }}
                      />
                    )}
                  </div>
                  
                  {/* Thumbnail Upload Button - Only on desktop */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      thumbnailInputRefs.current[index]?.click();
                    }}
                    className="absolute top-2 left-2 bg-blue-500 text-white rounded-full p-2 hover:bg-blue-600 transition-colors cursor-pointer hidden"
                    title="Upload custom thumbnail"
                  >
                    {uploadingThumbnails[index] ? (
                      <i className="ri-loader-4-line text-sm w-4 h-4 flex items-center justify-center animate-spin"></i>
                    ) : (
                      <i className="ri-image-add-line text-sm w-4 h-4 flex items-center justify-center"></i>
                    )}
                  </button>
                </>
              ) : (
                <img
                  src={uploadedFiles[index].url}
                  alt={`Upload ${index + 1}`}
                  className="w-full h-auto md:w-full md:h-full md:object-cover md:object-top"
                />
              )}
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center p-6">
              {isUploading ? (
                <div className="w-full text-center">
                  <i className="ri-upload-cloud-line text-4xl text-blue-400 animate-pulse mb-4 w-16 h-16 flex items-center justify-center mx-auto"></i>
                  <p className="text-sm text-gray-300">Uploading...</p>
                </div>
              ) : (
                <>
                  <i className={`${isVideo ? 'ri-video-line' : 'ri-image-line'} text-4xl text-gray-400 mb-4 w-16 h-16 flex items-center justify-center`}></i>
                  <p className="text-gray-300 text-center mb-4">
                    {isVideo ? 'Upload Video' : 'Upload Image'}
                  </p>
                  <p className="text-xs text-gray-400 text-center">
                    Click to upload
                  </p>
                </>
              )}
            </div>
          )}
        </div>
        
        <div className="w-full">
          <div className="relative">
            <textarea
              placeholder={t('leVeauDorCaption')}
              className="w-full h-32 text-base text-white placeholder-gray-400 border-0 rounded-lg p-4 outline-none resize-none leading-relaxed transition-all duration-200 bg-gray-800 shadow-none"
              value={captions[index] || ''}
              onChange={(e) => handleCaptionChange(index, e.target.value)}
              maxLength={1000}
            />
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    return () => {
      Object.values(captionTimeouts.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-400 animate-spin mb-4 w-16 h-16 flex items-center justify-center mx-auto"></i>
          <p className="text-lg text-gray-300">Loading your files and captions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <SEOHead
        title="Le Veau d'Or - Dava Nasr Portfolio"
        description="Le Veau d'Or creative design project by Dava Nasr. Graphic design and brand development work."
        keywords="Le Veau d'Or, graphic design, brand development, creative design, Dava Nasr"
        canonicalUrl={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/project/leveaudor`}
        schemaData={schemaData}
      />
      
      <Header />
      
      {/* Hamburger Menu */}
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="bg-black border border-white/20 shadow-lg rounded-lg p-2 md:p-6 hover:bg-gray-900 transition-colors duration-200 cursor-pointer"
        >
          <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} w-5 h-5 md:w-10 md:h-10 flex items-center justify-center text-white`} style={{ strokeWidth: '3px', fontWeight: 'bold' }}></i>
        </button>
        
        {isMenuOpen && (
          <div className="absolute top-full right-0 mt-1 bg-black border border-white/20 shadow-lg rounded-lg py-0.5 md:py-4 min-w-[100px] md:min-w-[200px]">
            {projectLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={() => handleMobileNavClick(link.href)}
                className="block px-3 py-1 md:px-6 md:py-3 text-xs md:text-xl font-medium text-white hover:bg-gray-900 hover:text-teal-400 transition-colors duration-200 cursor-pointer whitespace-nowrap text-right"
              >
                {link.name}
              </Link>
            ))}
          </div>
        )}
      </div>
      
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded mx-4 mt-4">
          <div className="flex items-center">
            <i className="ri-error-warning-line mr-2 w-5 h-5 flex items-center justify-center"></i>
            <strong>Error:</strong> {error}
          </div>
        </div>
      )}
      
      <div className="flex-1 flex flex-col items-center pt-32 pb-16">
        <h1 className="text-4xl md:text-8xl font-bold text-white mb-16">le veau d'or</h1>
      </div>

      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* First 3 JPG tiles in a row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {Array.from({ length: 3 }, (_, i) => renderUploadBox(i))}
          </div>
          
          {/* 4th tile (video) centered underneath */}
          <div className="flex justify-center">
            <div className="w-full max-w-md">
              {renderUploadBox(3)}
            </div>
          </div>
        </div>
      </section>

      {expandedBox !== null && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 items-center justify-center z-50 hidden md:flex"
          onClick={() => {
            setExpandedBox(null);
            resetZoom();
          }}
        >
          <div className="relative w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => {
                setExpandedBox(null);
                resetZoom();
              }}
              className="absolute top-4 left-4 text-white hover:text-gray-300 text-lg z-20 bg-black/50 rounded-full p-2"
            >
              <i className="ri-close-line w-6 h-6 flex items-center justify-center"></i>
            </button>

            {uploadedFiles[expandedBox] && uploadedFiles[expandedBox].type === 'image' && (
              <div className="absolute top-4 right-4 flex flex-col gap-2 z-20">
                <button
                  onClick={handleZoomIn}
                  className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-all"
                  disabled={zoomLevel >= 3}
                >
                  <i className="ri-zoom-in-line w-5 h-5 flex items-center justify-center"></i>
                </button>
                <button
                  onClick={handleZoomOut}
                  className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-all"
                  disabled={zoomLevel <= 0.5}
                >
                  <i className="ri-zoom-out-line w-5 h-5 flex items-center justify-center"></i>
                </button>
                <button
                  onClick={resetZoom}
                  className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-all"
                >
                  <i className="ri-fullscreen-exit-line w-5 h-5 flex items-center justify-center"></i>
                </button>
                <div className="bg-black/50 text-white px-2 py-1 rounded text-sm text-center">
                  {Math.round(zoomLevel * 100)}%
                </div>
              </div>
            )}
            
            {uploadedFiles[expandedBox] ? (
              <div className="relative overflow-hidden w-full h-full flex items-center justify-center">
                {uploadedFiles[expandedBox].type === 'video' ? (
                  <video
                    src={uploadedFiles[expandedBox].url}
                    className="max-w-[90vw] max-h-[90vh] object-contain"
                    controls
                    autoPlay
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <div 
                    className="overflow-hidden w-full h-full flex items-center justify-center cursor-move"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  >
                    <img
                      src={uploadedFiles[expandedBox].url}
                      alt={uploadedFiles[expandedBox].fileName}
                      className="object-contain transition-transform duration-200"
                      style={{
                        transform: `scale(${zoomLevel}) translate(${zoomPosition.x / zoomLevel}px, ${zoomPosition.y / zoomLevel}px)`,
                        maxWidth: zoomLevel === 1 ? '90vw' : 'none',
                        maxHeight: zoomLevel === 1 ? '90vh' : 'none',
                        cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                      }}
                      draggable={false}
                    />
                  </div>
                )}
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFileDelete(expandedBox);
                  }}
                  className="absolute bottom-4 right-4 bg-red-500 text-white p-3 rounded-full hover:bg-red-600 transition-all duration-300 z-20"
                >
                  <i className="ri-delete-bin-line w-5 h-5 flex items-center justify-center"></i>
                </button>
              </div>
            ) : (
              <div className="bg-gray-900 rounded-lg p-8 max-w-md mx-auto">
                <div className="text-center">
                  <i className={`${expandedBox === 3 ? 'ri-video-line' : 'ri-image-line'} text-6xl text-gray-400 mb-6 w-24 h-24 flex items-center justify-center mx-auto`}></i>
                  <h3 className="text-xl font-semibold mb-4 text-white">
                    {expandedBox === 3 ? 'Upload Video' : 'Upload Image'}
                  </h3>
                  <input
                    type="file"
                    accept={expandedBox === 3 ? "video/*" : "image/*"}
                    onChange={(e) => handleFileUpload(expandedBox, e)}
                    className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hidden file inputs */}
      {Array.from({ length: 4 }, (_, i) => (
        <input
          key={i}
          ref={(el) => (fileInputRefs.current[i] = el)}
          type="file"
          accept={i === 3 ? "video/*" : "image/*"}
          onChange={(e) => handleFileUpload(i, e)}
          className="hidden"
        />
      ))}

      {/* Hidden thumbnail inputs */}
      {Array.from({ length: 4 }, (_, i) => (
        <input
          key={`thumb-${i}`}
          ref={(el) => (thumbnailInputRefs.current[i] = el)}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={(e) => handleThumbnailUpload(i, e)}
          className="hidden"
        />
      ))}

      <Footer />
    </div>
  );
};

export default LeVeauDorPage;
